/**
 * 渠道管理API
 *
 * @description RTK Query API定义，用于渠道管理相关的数据获取和操作
 * @version 1.0.0
 * @author AI Assistant
 * @date 2025-09-24
 */

import { apiSlice } from './apiSlice';
import type {
  ChannelInfo,
  ChannelListResponse,
  ChannelDetailResponse,
  ChannelQueryParams,
  CreateChannelRequest,
  UpdateChannelRequest,
  QRCodeGenerationRequest,
  QRCodeGenerationResponse,
  ChannelStatistics,
} from '../../types/channel';

export const channelApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // 获取渠道列表
    getChannels: builder.query<ChannelListResponse, ChannelQueryParams | void>({
      query: params => {
        const searchParams = new URLSearchParams();

        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit)
          searchParams.append('limit', params.limit.toString());
        if (params?.search) searchParams.append('search', params.search);
        if (params?.status?.length) {
          params.status.forEach((status: string) =>
            searchParams.append('status', status)
          );
        }
        if (params?.type?.length) {
          params.type.forEach((type: string) =>
            searchParams.append('type', type)
          );
        }
        if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params?.sortOrder)
          searchParams.append('sortOrder', params.sortOrder);

        return {
          url: `/channels${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: result =>
        result?.data.channels
          ? [
              ...result.data.channels.map(({ _id, id }) => ({
                type: 'Channel' as const,
                id: _id || id,
              })),
              { type: 'Channel', id: 'LIST' },
            ]
          : [{ type: 'Channel', id: 'LIST' }],
    }),

    // 获取渠道详情
    getChannelById: builder.query<ChannelDetailResponse, string>({
      query: id => `/channels/${id}`,
      providesTags: (result, error, id) => [{ type: 'Channel', id }],
    }),

    // 创建渠道
    createChannel: builder.mutation<
      { success: boolean; message: string; data: { channel: ChannelInfo } },
      CreateChannelRequest
    >({
      query: channelData => ({
        url: '/channels',
        method: 'POST',
        body: channelData,
      }),
      invalidatesTags: [{ type: 'Channel', id: 'LIST' }],
    }),

    // 更新渠道
    updateChannel: builder.mutation<
      { success: boolean; message: string; data: { channel: ChannelInfo } },
      UpdateChannelRequest
    >({
      query: ({ _id, ...patch }) => ({
        url: `/channels/${_id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { _id }) => [
        { type: 'Channel', id: _id },
        { type: 'Channel', id: 'LIST' },
      ],
    }),

    // 删除渠道
    deleteChannel: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: id => ({
        url: `/channels/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Channel', id },
        { type: 'Channel', id: 'LIST' },
      ],
    }),

    // 生成二维码
    generateQRCode: builder.mutation<
      QRCodeGenerationResponse,
      QRCodeGenerationRequest
    >({
      query: params => ({
        url: `/channels/${params.channelId}/qrcode`,
        method: 'POST',
        body: {
          size: params.size || 200,
          format: params.format || 'png',
        },
      }),
      invalidatesTags: (result, error, { channelId }) => [
        { type: 'Channel', id: channelId },
      ],
    }),

    // 重新生成问卷链接
    regenerateQuestionnaireUrl: builder.mutation<
      {
        success: boolean;
        data: { questionnaireUrl: string; qrCodeUrl?: string };
      },
      string
    >({
      query: channelId => ({
        url: `/channels/${channelId}/regenerate-url`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, channelId) => [
        { type: 'Channel', id: channelId },
        { type: 'Channel', id: 'LIST' },
      ],
    }),

    // 获取渠道统计信息
    getChannelStatistics: builder.query<
      { success: boolean; data: ChannelStatistics[] },
      { dateRange?: [string, string]; channelIds?: string[] }
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();

        if (params.dateRange) {
          searchParams.append('startDate', params.dateRange[0]);
          searchParams.append('endDate', params.dateRange[1]);
        }
        if (params.channelIds?.length) {
          params.channelIds.forEach(id =>
            searchParams.append('channelIds', id)
          );
        }

        return {
          url: `/channels/statistics${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'Channel', id: 'STATISTICS' }],
    }),

    // 更新渠道状态
    updateChannelStatus: builder.mutation<
      { success: boolean; message: string },
      { channelId: string; status: 'active' | 'inactive' | 'suspended' }
    >({
      query: ({ channelId, status }) => ({
        url: `/channels/${channelId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { channelId }) => [
        { type: 'Channel', id: channelId },
        { type: 'Channel', id: 'LIST' },
      ],
    }),

    // 批量删除渠道
    batchDeleteChannels: builder.mutation<
      { success: boolean; message: string; deletedCount: number },
      string[]
    >({
      query: channelIds => ({
        url: '/channels/batch-delete',
        method: 'POST',
        body: { channelIds },
      }),
      invalidatesTags: [{ type: 'Channel', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetChannelsQuery,
  useGetChannelByIdQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useGenerateQRCodeMutation,
  useRegenerateQuestionnaireUrlMutation,
  useGetChannelStatisticsQuery,
  useUpdateChannelStatusMutation,
  useBatchDeleteChannelsMutation,
  // 懒加载查询
  useLazyGetChannelsQuery,
  useLazyGetChannelByIdQuery,
  useLazyGetChannelStatisticsQuery,
} = channelApi;
